import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "@formspree/react";
import {
  privatePartyPackages,
  formatPackagePrice,
  formatMoney,
  PRIVATE_PARTY_FEES,
} from "../data/packages";
import {
  PRICE_DISCLOSURE,
  SERVICE_FEE_POLICY,
  OTHER_CHARGES_POLICY,
  TAX_POLICY,
  CARD_PAYMENT_POLICY,
  DEPOSIT_POLICY,
  RESCHEDULING_POLICY,
  CANCELLATION_POLICY,
  BALANCE_POLICY,
} from "../data/bookingPolicies";
import { siteInfo } from "../data/siteInfo";
import PrivatePackageFacts from "../components/PrivatePackageFacts";
import { calculatePrivatePartyEstimate } from "../utils/privatePartyEstimate";
import FormField from "../components/FormField";
import FormError from "../components/FormError";
import InquirySuccess from "../components/InquirySuccess";
import Icon from "../components/Icon";
import "../css/BookNow.css";

const otherEvents = {
  weekday: "Non-private weekday party",
  rental: "Standalone location rental",
};
const validCount = (value) =>
  value !== "" && Number.isSafeInteger(Number(value)) && Number(value) >= 0;

function BookingEstimate({ pkg, estimate }) {
  const lines = [
    ["Package", pkg.price],
    [
      `Additional kids (${estimate.additionalKids} × $${PRIVATE_PARTY_FEES.additionalKid})`,
      estimate.additionalKidsCharge,
    ],
    [
      `Additional adults (${estimate.additionalAdults} × $${PRIVATE_PARTY_FEES.additionalAdult})`,
      estimate.additionalAdultsCharge,
    ],
    ["Package & guests subtotal", estimate.subtotal],
    ["Tax (10.25%)", estimate.tax],
  ];
  return (
    <section
      className="booking-estimate"
      aria-label="Private party estimate"
      aria-live="polite"
    >
      <h3>Your party estimate</h3>
      <dl>
        {lines.map(([label, amount]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{formatMoney(amount)}</dd>
          </div>
        ))}
        <div className="booking-estimate-amount">
          <dt>Estimated package + guests + tax</dt>
          <dd>{formatMoney(estimate.estimate)}</dd>
        </div>
      </dl>
      <p>
        Excludes optional add-ons and applicable payment fees. No payment is
        collected with this inquiry.
      </p>
      <input
        type="hidden"
        name="packageAndGuestsSubtotal"
        value={estimate.subtotal.toFixed(2)}
      />
      <input
        type="hidden"
        name="packageAndGuestsServiceFee"
        value={estimate.serviceFee.toFixed(2)}
      />
      <input
        type="hidden"
        name="estimatedTax"
        value={estimate.tax.toFixed(2)}
      />
      <input
        type="hidden"
        name="packageAndGuestsEstimateWithTax"
        value={estimate.estimate.toFixed(2)}
      />
    </section>
  );
}

export default function BookingForm() {
  const [state, handleSubmit] = useForm(siteInfo.formId);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPackage = privatePartyPackages.find(
    (pkg) => String(pkg.id) === searchParams.get("package"),
  );
  const otherEvent = searchParams.get("event");
  const selection = requestedPackage
    ? String(requestedPackage.id)
    : Object.hasOwn(otherEvents, otherEvent)
      ? otherEvent
      : "";
  const [kids, setKids] = useState(
    requestedPackage ? String(requestedPackage.includedKids) : "",
  );
  const [adults, setAdults] = useState(
    requestedPackage ? String(requestedPackage.includedAdults) : "",
  );
  const selectedPackage = privatePartyPackages.find(
    (pkg) => String(pkg.id) === selection,
  );
  useEffect(() => {
    const pkg = privatePartyPackages.find(
      (item) => String(item.id) === selection,
    );
    setKids(pkg ? String(pkg.includedKids) : "");
    setAdults(pkg ? String(pkg.includedAdults) : "");
  }, [selection]);
  const estimate =
    selectedPackage && validCount(kids) && validCount(adults)
      ? calculatePrivatePartyEstimate(selection, kids, adults)
      : null;
  const handlePackageChange = (event) => {
    const next = new URLSearchParams(searchParams);
    next.delete("package");
    next.delete("event");
    if (
      privatePartyPackages.some((pkg) => String(pkg.id) === event.target.value)
    )
      next.set("package", event.target.value);
    else if (Object.hasOwn(otherEvents, event.target.value))
      next.set("event", event.target.value);
    setSearchParams(next, { replace: true });
  };
  const today = new Date();
  const earliestDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  return (
    <div className="booking-page-container section-space">
      <div className="site-container">
        <header className="booking-page-intro">
          <p className="eyebrow">LET’S MAKE THEIR DAY</p>
          <h1>
            A little planning.
            <br />
            <span>A whole lot of magic.</span>
          </h1>
          <p>
            Share your celebration ideas and preferred date. Our team will
            confirm availability, pricing, and next steps.
          </p>
          <p className="booking-inquiry-note">
            <Icon name="mail" /> This is an inquiry, not a confirmed
            reservation. No payment is collected here.
          </p>
        </header>
        {state.succeeded ? (
          <div className="booking-success-wrap">
            <InquirySuccess booking />
          </div>
        ) : (
          <form
            className="booking-form"
            onSubmit={handleSubmit}
            aria-label="Party inquiry"
            aria-busy={state.submitting}
          >
            <input
              type="hidden"
              name="inquiryType"
              value="Party booking inquiry"
            />
            <FormError errors={state.errors} />
            <div className="booking-layout">
              <div className="booking-fields-card">
                <p className="form-intro">
                  All fields are required unless marked optional.
                </p>
                <fieldset disabled={state.submitting}>
                  <legend>
                    <span>01</span> Choose your celebration
                  </legend>
                  <FormField
                    id="package"
                    name="package"
                    label="Party package:"
                    as="select"
                    value={selection}
                    onChange={handlePackageChange}
                    required={false}
                    errors={state.errors}
                  >
                    <option value="">Help me choose / another event</option>
                    {privatePartyPackages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.title} — {formatPackagePrice(pkg.price)}
                      </option>
                    ))}
                    <option value="weekday">Non-private weekday party</option>
                    <option value="rental">Standalone location rental</option>
                  </FormField>
                  <input
                    type="hidden"
                    name="packageName"
                    value={
                      selectedPackage
                        ? selectedPackage.title
                        : otherEvents[selection] || "Other event / not selected"
                    }
                  />
                  <div className="form-grid booking-guest-fields">
                    <FormField
                      id="kids"
                      name="kids"
                      label="Number of Kids:"
                      type="number"
                      min="0"
                      step="1"
                      value={kids}
                      onChange={(event) => setKids(event.target.value)}
                      errors={state.errors}
                    />
                    <FormField
                      id="adults"
                      name="adults"
                      label="Number of Adults (excluding kids):"
                      type="number"
                      min="0"
                      step="1"
                      value={adults}
                      onChange={(event) => setAdults(event.target.value)}
                      errors={state.errors}
                    />
                  </div>
                  <input
                    type="hidden"
                    name="guests"
                    value={
                      validCount(kids) && validCount(adults)
                        ? Number(kids) + Number(adults)
                        : ""
                    }
                  />
                </fieldset>
                <fieldset disabled={state.submitting}>
                  <legend>
                    <span>02</span> Pick your preferred date
                  </legend>
                  <div className="form-grid">
                    <FormField
                      id="date"
                      name="date"
                      label="Preferred date"
                      type="date"
                      min={earliestDate}
                      errors={state.errors}
                    />
                    <div className="form-field">
                      <label htmlFor="time-hour">Preferred start time</label>
                      <div className="time-input">
                        <select
                          id="time-hour"
                          name="time-hour"
                          aria-label="Hour"
                          required
                          defaultValue=""
                        >
                          <option value="" disabled>
                            Hour
                          </option>
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {String(i + 1).padStart(2, "0")}
                            </option>
                          ))}
                        </select>
                        <span aria-hidden="true">:</span>
                        <select
                          id="time-minute"
                          name="time-minute"
                          aria-label="Minute"
                          defaultValue="00"
                          required
                        >
                          <option value="00">00</option>
                          <option value="30">30</option>
                        </select>
                        <select
                          id="time-ampm"
                          name="time-ampm"
                          aria-label="AM or PM"
                          defaultValue=""
                          required
                        >
                          <option value="" disabled>
                            AM/PM
                          </option>
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                      <p className="form-help">
                        Our team will confirm availability.
                      </p>
                    </div>
                  </div>
                </fieldset>
                <fieldset disabled={state.submitting}>
                  <legend>
                    <span>03</span> A few details about you
                  </legend>
                  <div className="form-grid">
                    <FormField
                      id="firstName"
                      label="First name"
                      autoComplete="given-name"
                      errors={state.errors}
                    />
                    <FormField
                      id="lastName"
                      label="Last name"
                      autoComplete="family-name"
                      errors={state.errors}
                    />
                    <FormField
                      id="email"
                      label="Email address"
                      type="email"
                      autoComplete="email"
                      errors={state.errors}
                    />
                    <FormField
                      id="phone"
                      label="Phone number"
                      type="tel"
                      autoComplete="tel"
                      errors={state.errors}
                    />
                    <FormField
                      id="message"
                      label="Tell us about your celebration"
                      as="textarea"
                      help="Share a theme, questions, or optional add-ons you’d like to discuss."
                      errors={state.errors}
                      className="form-field-wide"
                    />
                  </div>
                </fieldset>
              </div>
              <aside
                className="booking-summary-column"
                aria-label="Package summary and fees"
              >
                {selectedPackage ? (
                  <div className="booking-package-summary">
                    <p className="eyebrow">YOUR CELEBRATION</p>
                    <h2>{selectedPackage.title}</h2>
                    <p>{selectedPackage.description}</p>
                    <p className="booking-package-price">
                      {formatPackagePrice(selectedPackage.price)}
                    </p>
                    <p className="private-party-price-note">
                      {PRICE_DISCLOSURE}
                    </p>
                    <PrivatePackageFacts pkg={selectedPackage} />
                    <p className="booking-included-pizzas">
                      Includes {selectedPackage.includedPizzas} large
                      one-topping pizzas.
                    </p>
                  </div>
                ) : (
                  <div className="booking-package-summary">
                    <span className="round-icon">
                      <Icon name="crown" />
                    </span>
                    <h2>
                      {otherEvents[selection] || "Let’s find your perfect fit."}
                    </h2>
                    <p>
                      {selection
                        ? "Share a little about your plans. We’ll follow up with pricing and availability."
                        : "Choose a private package to see guest allowances and a tax estimate, or let our team help you choose."}
                    </p>
                    <p className="private-party-price-note">
                      {PRICE_DISCLOSURE}
                    </p>
                  </div>
                )}
                {estimate && (
                  <BookingEstimate pkg={selectedPackage} estimate={estimate} />
                )}
                <div className="booking-fee-disclosure">
                  <h3>Before you book</h3>
                  <p>{SERVICE_FEE_POLICY}</p>
                  <p>{TAX_POLICY} Optional add-ons are priced separately.</p>
                  <p className="card-payment-note">
                    <strong>{CARD_PAYMENT_POLICY}</strong>
                  </p>
                  <details>
                    <summary>Deposits, payments & policies</summary>
                    <p>
                      {DEPOSIT_POLICY} {BALANCE_POLICY}
                    </p>
                    <p>
                      {RESCHEDULING_POLICY} {CANCELLATION_POLICY}
                    </p>
                    <p>{OTHER_CHARGES_POLICY}</p>
                  </details>
                  <Link to="/packagesdetail#booking-policies">
                    Read all booking policies, including applicable event and
                    vendor fees
                  </Link>
                </div>
              </aside>
            </div>
            <div className="booking-submit-card">
              <p>
                Sending this inquiry does not reserve a date. We’ll contact you
                to discuss your celebration.
              </p>
              <button
                className="button button-primary"
                type="submit"
                disabled={state.submitting}
              >
                {state.submitting ? "Sending inquiry…" : "Send party inquiry"}
                <Icon />
              </button>
              <p className="form-help">
                Need a hand?{" "}
                <a href={siteInfo.phoneHref}>Call {siteInfo.phone}</a>. By
                sending, you agree to our{" "}
                <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
